import axios from "axios";
import { _axios } from "../../utils/axios";
import userImg from "../../assets/images/user.svg";
import dayjs from "dayjs";
import Multiselect from "multiselect-react-dropdown";
import { useEffect, useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { FileUploader } from "react-drag-drop-files";
import { FormProvider, useForm } from "react-hook-form";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { SpinnerCircular } from "spinners-react";
import { Dialog } from "../../components/CommonComponents/Dialog/Dialog";
import Input from "../../components/CommonComponents/Input/Input";
import { tableCustomStyles } from "../../components/CommonComponents/Table/Table";
import { BASE_URL } from "../../constants/Constants";
import { useSetStuffToRenderOnNavbar } from "../../contexts/NavbarCommonSpace";
import { isLevel3 } from "../../hoc/isLevel3";
import { TApplicationField, TApplicationFieldOption } from "../../hooks/application-fields.hooks";
import {
    TApplication,
    TPopulatedForm,
    useApplicationFormSteps,
    useNameOfServiceField,
    useServiceConditionField,
} from "../../hooks/applications.hooks";
import { useToggle } from "../../hooks/common";
import {
    TReviewWithReviewer,
    TSavedDemandNoteData,
    level3ApplicationsKeyStore,
    useApplicationForLevel3,
    useApplicationOverride,
    useAutoFillDemandNoteData,
    useForwardApplicationForLevel3,
    useLevel3AllReviews,
    useLevel3DecisionMaps,
    useLevel3LastReview,
    useLevel3RejectApplication,
    useLevel3ReturnApplication,
    useLevel3UpdateDocumentDecision,
    useLevel3UpdateNocDecision,
    useSaveDemandNoteDataForLevel3,
    useSaveLevel3Review,
    useSavedDemandNoteDataForLevel3,
} from "../../hooks/level-3-application-approval.hooks";
import uploadFileToBlob from "../../utils/azure-storage-blob";
import { downloadResource, removeEmptyValues } from "../../utils/common";
import { formatDate } from "../../utils/date";
import style from '../superAdmin/logo/logo.module.css';
import { RenderAllReviews } from "./RenderAllReviews";
import Select from "../../components/CommonComponents/Select/Select";
import { useQueryClient } from "@tanstack/react-query";
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { DocumentPreviewDialog } from "../../components/CommonComponents/DialogConfirmation/DocPreviewDialog";
import { DeleteButton, DocDecisionL3Button, DownloadButton, NocButton, ViewButton } from "../../components/CommonComponents/EditComponent/EditComponent";


const LevelThreeApplicationDoc = isLevel3(() => {
    const location = useLocation();
    const [screen, setScreen] = useState<"docs" | "review" | "generate-doc" | "upload-doc">(
        "docs"
    );

    const id = +location.state?.id;

    const decisionMapQuery = useLevel3DecisionMaps({ id });

    const {
        isError: isError1,
        isFetching: isFetching1,
        data: application,
    } = useApplicationForLevel3({ id });

    const {
        data: result,
        isError: isError2,
        isFetching: isFetching2,
    } = useApplicationFormSteps(Number(application?.name_of_service));
    const autoFill = useAutoFillDemandNoteData();
    const serviceQuery = useNameOfServiceField();
    const nameOfService = serviceQuery.data?.options?.length ?
        serviceQuery.data.options.find((e) => e?.id === application?.name_of_service) : ''

    const condition = useServiceConditionField({ name_of_service: (nameOfService as TApplicationFieldOption)?.name })
    const steps = result?.data || [];

    const isFetching = isFetching1 || isFetching2 || decisionMapQuery.isLoading;
    const isError =
        decisionMapQuery.isError ||
        isError1 ||
        isError2 ||
        (!isFetching && (!application || !steps?.length));

    const setStuff = useSetStuffToRenderOnNavbar();

    useEffect(() => {
        setStuff(
            <div className="md:ml-auto md:mr-8">
                <p className="text-grey-600">
                    <span className="w-28 font-semibold inline-block">
                        Application ID
                    </span>
                    <span className="text-grey-600 font-semibold mx-2">:</span>
                    <span>{application?.applicationId || "Nil"}</span>
                </p>
                <p className="text-grey-600 flex items-center">
                    <span className="w-28 text-grey-600 font-semibold inline-block">
                        Status
                    </span>
                    <span className="text-grey-600 font-semibold mx-2">:</span>
                    <span className="text-primary">{application?.status || "Nil"}</span>
                </p>
            </div>
        );
        return () => setStuff(null);
    }, [setStuff, application]);

    const { documentDecisions, nocDecisions } = decisionMapQuery.data || {};

    if (isNaN(id)) {
        return <Navigate to={"/user-applications"} replace />;
    }

    if (isFetching || isError || !application) {
        return (
            <div className="flex justify-center items-center p-8">
                {isError ? (
                    <p>Something went wrong while fetching info!</p>
                ) : (
                    <SpinnerCircular />
                )}
            </div>
        );
    }

    let toRender = null;

    switch (screen) {
        case "docs": {
            toRender = (
                <ViewAndValidateDocumentsAndNOCs
                    application={application}
                    steps={steps}
                    documentDecisions={documentDecisions}
                    nocDecisions={nocDecisions}
                    onNext={() => setScreen("review")}
                />
            );
            break;
        }
        case "review": {
            toRender = (
                <Review
                    steps={steps}
                    application={application}
                    onBack={() => setScreen("docs")}
                    onNext={() => setScreen("generate-doc")}
                    allDocsApproved={Object.values(documentDecisions || {})
                        .concat(Object.values(nocDecisions || {}))
                        .every((e) => !!e)}
                />
            );
            break;
        }
        case "generate-doc": {
            toRender = (
                <GenerateDemandNoteWrapper
                    application={application}
                    autoFill={autoFill}
                    condition={condition?.data?.[0] || {}}
                    onBack={() => setScreen("docs")}
                    onDownload={() => setScreen("upload-doc")}
                />
            );
            break;
        }
        case "upload-doc": {
            toRender = (
                <UploadDemandNoteWrapper
                    application={application}
                    onBack={() => setScreen("docs")}
                />
            );
            break;
        }
    }

    return (
        <>
            <div className="px-4 pb-4 md:p-4">
                {/* <div className="bg-white p-4 sm:p-8 rounded-2xl">{toRender}</div> */}
                <div className={`${style.IFrameOutCss} bg-white p-4 sm:p-8 rounded-xl shadow-lg border border-gray-300`}>{toRender}</div>
            </div>
        </>
    );
});

type GenerateDemandNoteProps = {
    onBack: () => unknown;
    application: TApplication & Record<string, unknown>;
    autoFill?: any;
    condition?: any;
    onDownload?: () => unknown;
};

function GenerateDemandNoteWrapper({
    application,
    autoFill,
    condition,
    onBack,
    onDownload
}: GenerateDemandNoteProps) {
    const isThereAConst = application.is_there_a_construction;
    console.log("yes/no", isThereAConst)

    const lastReviewQuery: any = useLevel3LastReview({ id: application.id });
    const reviewId = lastReviewQuery.data?.review?.id;
    const savedDemandNoteDataQuery = useSavedDemandNoteDataForLevel3({
        applicationId: application.id,
        reviewId: reviewId,
        isThereAConst,
    });

    console.log('savedDemandNoteDataQuery', savedDemandNoteDataQuery)

    const isFetching =
        lastReviewQuery.isFetching || savedDemandNoteDataQuery.isFetching;
    const isError =
        lastReviewQuery.isError || savedDemandNoteDataQuery.isError || !reviewId;

    if (isFetching || isError) {
        return (
            <div className="flex justify-center items-center p-8">
                {isError ? (
                    <p>Something went wrong while fetching info!</p>
                ) : (
                    <SpinnerCircular />
                )}
            </div>
        );
    }

    // return (
    //     <GenerateDemandNote
    //         reviewId={reviewId}
    //         application={application}
    //         autoFill={autoFill}
    //         condition={condition}
    //         onBack={onBack}
    //         defaultValues={savedDemandNoteDataQuery.data}
    //         onDownload={() => onDownload?.()}
    //     />
    // );
    if (isThereAConst) {
        return (
            <GenerateDemandNoteBuildingPlan
                reviewId={reviewId}
                application={application}
                autoFill={autoFill}
                condition={condition}
                onBack={onBack}
                defaultValues={savedDemandNoteDataQuery.data}
                onDownload={() => onDownload?.()}
            />
        );
    } else {
        return (
            <GenerateDemandNote
                reviewId={reviewId}
                application={application}
                autoFill={autoFill}
                condition={condition}
                onBack={onBack}
                defaultValues={savedDemandNoteDataQuery.data}
                onDownload={() => onDownload?.()}
            />
        );
    };
}

function GenerateDemandNote({
    defaultValues,
    application,
    autoFill,
    condition,
    onBack,
    reviewId,
    onDownload
}: GenerateDemandNoteProps & {
    defaultValues?: TSavedDemandNoteData;
    reviewId: number;
    onDownload: () => unknown;
}) {
    const isThereAConst = application.is_there_a_construction;
    console.log("yes/no", isThereAConst)

    const [pdfToView, setPdfToView] = useState<null | string>(null);
    const forwardMutation = useForwardApplicationForLevel3();
    const saveDataMutation = useSaveDemandNoteDataForLevel3();
    const {
        data: overrideData
    } = useApplicationOverride(application?.applicationId);
    console.log('application', application)
    const formMethods = useForm({
        defaultValues: {
            ...removeEmptyValues(defaultValues ?? {}),
            applicantName: application.plot_owner_s_name || "",
            layoutName: application.unauthorized_layout_name || "",
            mauja: application.mauja || "",
            plotNo: application.plot_no || "",
            area: application.area_of_plot || 0,
            unit_area: application.unit_area_of_plot || '',
            constructionArea: application.construction_area || 0,
            dueDate: defaultValues?.dueDate
                ? dayjs(defaultValues.dueDate).format("YYYY-MM-DD")
                : null,
            khasaraNo: application?.khasara_no || "",
            municipality: autoFill?.data?.[0]?.municipal_council_name || '',
            tehsil: autoFill?.data?.[0]?.tahasil_name || '',
            district: autoFill?.data?.[0]?.district_name || '',
            logo: autoFill?.data?.[0]?.demand_note_logo || '',
            headerText: autoFill?.data?.[0]?.demand_note_header_text || '',
            conditions: overrideData?.data && condition?.status === 1 ? condition?.condition : defaultValues?.conditions,

        },
        mode: "onBlur",
        shouldFocusError: true,
    });

    const { formState } = formMethods;

    const [isEditable, setIsEditable] = useState({
        particular1Total: false,
        particular2Total: false,
        totalAmount: false,
    });

    type EditableFields = 'particular1Total' | 'particular2Total' | 'totalAmount';
    type AllFields = EditableFields | 'particular1Rate' | 'particular2Rate' | 'area';

    const toggleEditable = (field: EditableFields) => {
        setIsEditable((prev) => ({ ...prev, [field]: !prev[field] }));
    };

    const onChangeHandler = (field: AllFields, event: React.ChangeEvent<any>) => {
        const value = event.target.value.trim();
        const numericValue = value.replace(/[^\d.]/g, "");
        formMethods.setValue(field, numericValue);

        const particular1Total =
            Number(formMethods.getValues('particular1Rate') || 0) *
            Number(formMethods.getValues('area') || 1);
        const particular2Total =
            Number(formMethods.getValues('particular2Rate') || 0) *
            Number(formMethods.getValues('area') || 0);
        const scrutinyCharges = Number(formMethods.getValues('scrutinyCharges') || 0);
        const totalAmount = particular1Total + particular2Total + scrutinyCharges;

        if (field === 'particular1Rate' || field === 'area') {
            formMethods.setValue('particular1Total', particular1Total);
        }
        if (field === 'particular2Rate' || field === 'area') {
            formMethods.setValue('particular2Total', particular2Total);
        }
        formMethods.setValue('totalAmount', totalAmount);
    };

    const onChangeParticular1Rate = (event: React.ChangeEvent<any>) => {
        const value = event.target.value.trim();
        console.log('value', value)
        // Remove non-numeric characters from the input value
        const numericValue = value.replace(/[^\d.]/g, '');
        console.log('numericValue', numericValue)
        formMethods.setValue("particular1Rate", numericValue);
        const particular1Total = Number(numericValue) * Number(formMethods?.getValues()?.area || 1)
        formMethods.setValue("particular1Total", particular1Total);
        const totalAmount = particular1Total + Number(formMethods?.getValues()?.scrutinyCharges || 0) + Number(formMethods?.getValues()?.particular2Total || 0)
        formMethods.setValue("totalAmount", totalAmount);
    }

    const onChangeParticular1Total = (event: React.ChangeEvent<any>) => {
        const value = event.target.value.trim();
        // Remove non-numeric characters from the input value
        const numericValue = value.replace(/[^\d.]/g, '');
        formMethods.setValue("particular1Total", numericValue);
        const totalAmount = Number(numericValue) + Number(formMethods?.getValues()?.scrutinyCharges || 0) + Number(formMethods?.getValues()?.particular2Total || 0)
        formMethods.setValue("totalAmount", totalAmount);
    }

    const onChangeParticular2Rate = (event: React.ChangeEvent<any>) => {
        const value = event.target.value.trim();
        // Remove non-numeric characters from the input value
        const numericValue = value.replace(/[^\d.]/g, '');
        formMethods.setValue("particular2Rate", numericValue);
        const particular2Total = Number(numericValue) * Number(formMethods?.getValues()?.area || 1)
        formMethods.setValue("particular2Total", particular2Total);
        const totalAmount = particular2Total + Number(formMethods?.getValues()?.particular1Total || 0) + Number(formMethods?.getValues()?.scrutinyCharges || 0)
        formMethods.setValue("totalAmount", totalAmount);
    }

    const onChangeParticular2Total = (event: React.ChangeEvent<any>) => {
        const value = event.target.value.trim();
        // Remove non-numeric characters from the input value
        const numericValue = value.replace(/[^\d.]/g, '');
        formMethods.setValue("particular2Total", numericValue);
        const totalAmount = Number(numericValue) + Number(formMethods?.getValues()?.particular1Total || 0) + Number(formMethods?.getValues()?.scrutinyCharges || 0)
        formMethods.setValue("totalAmount", totalAmount);
    }

    const onChangeScrutinyCharges = (event: React.ChangeEvent<any>) => {
        const value = event.target.value.trim();
        // Remove non-numeric characters from the input value
        const numericValue = value.replace(/[^\d.]/g, '');
        formMethods.setValue("scrutinyCharges", numericValue);
        const totalAmount = Number(numericValue) + Number(formMethods?.getValues()?.particular1Total || 0) + Number(formMethods?.getValues()?.particular2Total || 0)
        formMethods.setValue("totalAmount", totalAmount);
    }

    const onChangeTotalAmount = (event: React.ChangeEvent<any>) => {
        const value = event.target.value.trim();
        // Remove non-numeric characters from the input value
        const numericValue = value.replace(/[^\d.]/g, '');
        formMethods.setValue("totalAmount", numericValue);
    }

    const [isEditableConditions, setIsEditableConditions] = useState(false);
    const toggleEditableConditions = () => {
        setIsEditableConditions((prev) => !prev);
    };


    return (
        <FormProvider {...formMethods}>
            <h4
                className={`text-center text-grey-600 font-semibold mb-8 text-2xl ${pdfToView ? "hidden" : ""
                    }`}
            >
                Demand Note - Site Plan
            </h4>

            <form
                id="create-demand-note"
                className={`grid grid-cols-2 gap-4 ${pdfToView ? "hidden" : ""}`}
                noValidate
                onSubmit={formMethods.handleSubmit((data) =>
                    saveDataMutation.mutate(
                        {
                            applicationId: application.id,
                            reviewId,
                            data,
                            isThereAConst: isThereAConst ?? false,
                        },
                        {
                            onSuccess: (result) => {
                                if (Array.isArray(result?.data?.data)) {
                                    const blob = new Blob(
                                        [new Uint8Array(result.data.data).buffer],
                                        { type: "application/pdf" }
                                    );
                                    setPdfToView(URL.createObjectURL(blob));
                                }
                            },
                        }
                    )
                )}
            >
                <div className="col-span-2 md:col-span-1">
                    <Input
                        disabled
                        label="Applicant Name"
                        placeholder="Applicant Name"
                        {...formMethods.register("applicantName")}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        disabled
                        label="Layout Name"
                        placeholder="Layout Name"
                        {...formMethods.register("layoutName")}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        disabled
                        label="Mauza"
                        placeholder="Mauza"
                        {...formMethods.register("mauja")}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        disabled
                        label="Khasara No"
                        placeholder="Khasara No"
                        {...formMethods.register("khasaraNo")}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <div className="flex items-end justify-between gap-4">
                        <Input
                            disabled
                            type="number"
                            // containerClass="basis-3/4"
                            containerClass="basis-3/5 md:basis-3/4"
                            label="Plot Area"
                            placeholder="Plot Area"
                            {...formMethods.register("area", { valueAsNumber: true })}
                        />
                        <Select
                            // containerClass="basis-1/4"
                            containerClass="basis-2/5 md:basis-1/4"
                            disabled
                            {...formMethods.register("unit_area")}
                        >
                            <option hidden></option>
                            <option value="sq. mt.">sq. mt.</option>
                            <option value="sq. ft.">sq. ft.</option>
                        </Select>
                    </div>
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        disabled
                        label="Built-up Area [sq. mt.]"
                        placeholder="Built-up Area [sq. mt.]"
                        {...formMethods.register("constructionArea", {
                            valueAsNumber: true,
                        })}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        disabled
                        label="Plot No"
                        placeholder="Plot No"
                        {...formMethods.register("plotNo")}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        type="date"
                        label="Due Date"
                        placeholder="Due Date"
                        {...formMethods.register("dueDate", { valueAsDate: true })}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        required
                        label="Rate for Regularization charges for residential plot"
                        placeholder="Rate for Regularization charges for residential plot"
                        error={!!formState.errors?.particular1Rate}
                        helperText={formState?.errors?.particular1Rate?.message?.toString()}
                        {...formMethods.register("particular1Rate", {
                            required: "* Required",
                            valueAsNumber: true,
                        })}

                        onChange={onChangeParticular1Rate}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <label className="flex items-center">
                        <span className="mr-2 p-1 bold"><b>Regularization charges for residential plot</b></span>

                        <i
                            className={`fa ${isEditable.particular1Total ? 'fa-lock' : 'fa-pencil'} cursor-pointer`}
                            onClick={() => toggleEditable('particular1Total')}
                        ></i>
                    </label>
                    <Input
                        required
                        type="number"
                        label=""
                        placeholder="Regularization charges for residential plot"
                        error={!!formState.errors?.particular1Total}
                        helperText={formState?.errors?.particular1Total?.message?.toString()}
                        {...formMethods.register("particular1Total", {
                            required: "* Required",
                            valueAsNumber: true,
                        })}
                        // onChange={(e) => onChangeHandler('particular1Total', e)}
                        onChange={onChangeParticular1Total}

                        disabled={!isEditable.particular1Total}
                    />

                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        required
                        label="Rate for Development charges for residential plot"
                        placeholder="Rate for Development charges for residential plot"
                        error={!!formState.errors?.particular2Rate}
                        helperText={formState?.errors?.particular2Rate?.message?.toString()}
                        {...formMethods.register("particular2Rate", {
                            required: "* Required",
                            valueAsNumber: true,
                        })}
                        onChange={onChangeParticular2Rate}
                    />

                </div>
                <div className="col-span-2 md:col-span-1">
                    <label className="flex items-center">
                        <span className="mr-2 p-1">
                            <b>Development charges for residential plot</b> </span>
                        <i
                            className={`fa ${isEditable.particular2Total ? 'fa-lock' : 'fa-pencil'} cursor-pointer`}
                            onClick={() => toggleEditable('particular2Total')}
                        ></i>
                    </label>
                    <Input
                        required
                        type="number"
                        label=""
                        placeholder="Development charges for residential plot"
                        error={!!formState.errors?.particular2Total}
                        helperText={formState?.errors?.particular2Total?.message?.toString()}
                        {...formMethods.register("particular2Total", {
                            required: "* Required",
                            valueAsNumber: true,
                        })}
                        // onChange={(e) => onChangeHandler('particular2Total', e)}
                        onChange={onChangeParticular2Total}

                        disabled={!isEditable.particular2Total}
                    />

                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        required
                        label="Scrutiny Charges"
                        placeholder="Scrutiny Charges"
                        error={!!formState.errors?.scrutinyCharges}
                        helperText={formState?.errors?.scrutinyCharges?.message?.toString()}
                        {...formMethods.register("scrutinyCharges", {
                            required: "* Required",
                            valueAsNumber: true,
                        })}
                        onChange={onChangeScrutinyCharges}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <label className="flex items-center">
                        <span className="mr-2 p-1">
                            <b> Total Demand Note Amount</b> </span>
                        <i
                            className={`fa ${isEditable.totalAmount ? 'fa-lock' : 'fa-pencil'} cursor-pointer`}
                            onClick={() => toggleEditable('totalAmount')}
                        ></i>
                    </label>
                    <Input
                        required
                        type="number"
                        label=""
                        placeholder="Total Demand Note Amount"
                        error={!!formState.errors?.totalAmount}
                        helperText={formState?.errors?.totalAmount?.message?.toString()}
                        {...formMethods.register("totalAmount", {
                            required: "* Required",
                            valueAsNumber: true,
                        })}
                        // onChange={(e) => onChangeHandler('totalAmount', e)}
                        onChange={onChangeTotalAmount}

                        disabled={!isEditable.totalAmount}
                    />

                </div>

                <div className="col-span-2 md:col-span-1">
                    <Input
                        disabled
                        label="Municipal Council Name"
                        placeholder="Municipal Council Name"
                        error={!!formState.errors?.municipality}
                        helperText={formState?.errors?.municipality?.message?.toString()}
                        {...formMethods.register("municipality", {
                            required: "* Required",
                        })}
                    />
                </div>

                <div className="col-span-2 md:col-span-1">
                    <Input
                        disabled
                        label="Office Address: Tahasil"
                        placeholder="Office Address: Tahasil"
                        error={!!formState.errors?.tehsil}
                        helperText={formState?.errors?.tehsil?.message?.toString()}
                        {...formMethods.register("tehsil", { required: "* Required" })}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        disabled
                        label="Office Address: District"
                        placeholder="Office Address: District"
                        error={!!formState.errors?.district}
                        helperText={formState?.errors?.district?.message?.toString()}
                        {...formMethods.register("district", { required: "* Required" })}
                    />
                </div>
                <div className="col-span-2">
                    <label className="flex items-center">
                        <span className="text-base font-bold mb-2">Conditions For Demand Note</span>
                        <i
                            className={`fa ${isEditableConditions ? 'fa-lock' : 'fa-pencil'} cursor-pointer ml-2`}
                            onClick={toggleEditableConditions}
                        ></i>
                    </label>
                    <textarea
                        placeholder="Conditions for Demand Note"
                        {...formMethods.register("conditions")}
                        rows={5}
                        className={`h-auto w-100 ${isEditableConditions ? 'bg-white' : 'bg-gray-200'}`}
                        disabled={!isEditableConditions}
                    />
                </div>
                <div className="col-span-2 flex justify-center space-x-4 items-center mt-4">
                    <button
                        type="button"
                        onClick={onBack}
                        disabled={saveDataMutation.isPending || forwardMutation.isPending}
                        className="bg-black px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
                    >
                        Back
                    </button>
                    <button
                        form="create-demand-note"
                        type="submit"
                        disabled={saveDataMutation.isPending || forwardMutation.isPending}
                        className="bg-primary px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
                    >
                        {"Save And Preview"}
                    </button>
                </div>
            </form>
            {!!pdfToView && (
                <>
                    <iframe
                        title="PDF Viewer"
                        // className="w-full h-full min-h-screen hidden sm:block"
                        className={style.IFrameCss}
                        src={pdfToView + "#toolbar=0"}
                    />
                    <div className="flex items-center justify-center sm:hidden py-36 bg-gray-200 flex-col gap-4">
                        <p>Demand Note</p>
                        <a
                            className="inline-block text-white py-2 px-4 rounded-full bg-blue-500"
                            download={`${application.applicationId}-Demand-Note.pdf`}
                            href={pdfToView}
                        >
                            Download
                        </a>
                    </div>
                    <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-8">
                        <button
                            type="button"
                            onClick={() => setPdfToView(null)}
                            disabled={saveDataMutation.isPending || forwardMutation.isPending}
                            className="bg-primary px-6 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500 w-full sm:w-auto"
                        >
                            Back
                        </button>
                        <button
                            type="button"
                            className="bg-primary px-6 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500 w-full sm:w-auto"
                        >
                            <a href={pdfToView} download className="hover:text-white">Download</a>
                        </button>
                        <button
                            form="create-demand-note"
                            type="submit"
                            onClick={(data) => {
                                onDownload()
                            }}
                            disabled={saveDataMutation.isPending || forwardMutation.isPending}
                            className="bg-primary px-6 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500 w-full sm:w-auto"
                        >
                            {forwardMutation.isPending ? "Next" : "Next"}
                        </button>
                    </div>
                </>
            )}
        </FormProvider>
    );
}

function GenerateDemandNoteBuildingPlan({
    defaultValues,
    application,
    autoFill,
    condition,
    onBack,
    reviewId,
    onDownload
}: GenerateDemandNoteProps & {
    defaultValues?: TSavedDemandNoteData;
    reviewId: number;
    onDownload: () => unknown;
}) {
    const navigate = useNavigate();
    const isThereAConst = application.is_there_a_construction;
    console.log("yes/no", isThereAConst)
    const [pdfToView, setPdfToView] = useState<null | string>(null);
    const forwardMutation = useForwardApplicationForLevel3();
    const saveDataMutation = useSaveDemandNoteDataForLevel3();
    const {
        data: overrideData
    } = useApplicationOverride(application?.applicationId);
    console.log('application', application)

    const formMethods = useForm({
        defaultValues: {
            ...removeEmptyValues(defaultValues ?? {}),
            applicantName: application.plot_owner_s_name || "",
            layoutName: application.unauthorized_layout_name || "",
            mauja: application.mauja || "",
            plotNo: application.plot_no || "",
            area: application.area_of_plot || 0,
            unit_area: application.unit_area_of_plot || '',
            constructionArea: application.construction_area || 0,
            dueDate: defaultValues?.dueDate
                ? dayjs(defaultValues.dueDate).format("YYYY-MM-DD")
                : null,
            khasaraNo: application?.khasara_no || "",
            municipality: autoFill?.data?.[0]?.municipal_council_name || '',
            tehsil: autoFill?.data?.[0]?.tahasil_name || '',
            district: autoFill?.data?.[0]?.district_name || '',
            logo: autoFill?.data?.[0]?.demand_note_logo || '',
            headerText: autoFill?.data?.[0]?.demand_note_header_text || '',
            conditions: overrideData?.data && condition?.status === 1 ? condition?.condition : defaultValues?.conditions,
        },
        mode: "onBlur",
        shouldFocusError: true,
    });

    const { formState } = formMethods;

    const [isEditable, setIsEditable] = useState({
        particular1Total: false,
        particular2Total: false,
        totalAmount: false,
        buildingDevelopmentRRLandTotal: false,
        labourCessRRConstructionTotal: false,
        chargesForConstructioInSideMargineRRLandTotal: false,
    });

    type EditableFields = "particular1Total" | "particular2Total" | "totalAmount" | "buildingDevelopmentRRLandTotal" | "labourCessRRConstructionTotal" | "chargesForConstructioInSideMargineRRLandTotal";
    type AllFields =
        | EditableFields
        | "particular1Rate"
        | "particular2Rate"
        | "area"
        | "buildingDevelopmentRRLandRate"
        | "areaExcessOfBasicFSI"
        | "labourCessRRConstructionRate"
        | "chargesForConstructioInSideMargineRRLandRate"
        | "rainWaterHarwesting"
        | "securityDeposite";

    const toggleEditable = (field: EditableFields) => {
        setIsEditable((prev) => ({ ...prev, [field]: !prev[field] }));
    };

    const onChangeHandler = (field: AllFields, event: React.ChangeEvent<any>) => {
        const value = event.target.value.trim();
        const numericValue = value.replace(/[^\d.]/g, "");
        formMethods.setValue(field, numericValue);

        const particular1Total =
            Number(formMethods.getValues("particular1Rate") || 0) *
            Number(formMethods.getValues("area") || 1);
        const particular2Total =
            Number(formMethods.getValues("particular2Rate") || 0) *
            Number(formMethods.getValues("area") || 0);
        const buildingDevelopmentRRLandTotal =
            Number(formMethods.getValues("buildingDevelopmentRRLandRate") || 0) *
            Number(formMethods.getValues("area") || 0);
        const labourCessRRConstructionTotal =
            Number(formMethods.getValues("labourCessRRConstructionRate") || 0) *
            Number(formMethods.getValues("area") || 0);
        const chargesForConstructioInSideMargineRRLandTotal =
            Number(formMethods.getValues("chargesForConstructioInSideMargineRRLandRate") || 0) *
            Number(formMethods.getValues("area") || 0);
        const areaExcessOfBasicFSI =
            Number(formMethods.getValues("areaExcessOfBasicFSI") || 0);
        const rainWaterHarwesting =
            Number(formMethods.getValues("rainWaterHarwesting") || 0);
        const securityDeposite =
            Number(formMethods.getValues("securityDeposite") || 0);
        const scrutinyCharges =
            Number(formMethods.getValues("scrutinyCharges") || 0);
        const totalAmount = particular1Total + particular2Total + buildingDevelopmentRRLandTotal + labourCessRRConstructionTotal + chargesForConstructioInSideMargineRRLandTotal +
            areaExcessOfBasicFSI +
            rainWaterHarwesting +
            securityDeposite +
            scrutinyCharges;

        if (field === "particular1Rate" || field === "area") {
            formMethods.setValue("particular1Total", particular1Total);
        }
        if (field === "particular2Rate" || field === "area") {
            formMethods.setValue("particular2Total", particular2Total);
        }
        if (field === "buildingDevelopmentRRLandRate" || field === "area") {
            formMethods.setValue("buildingDevelopmentRRLandTotal", buildingDevelopmentRRLandTotal);
        }
        if (field === "labourCessRRConstructionRate" || field === "area") {
            formMethods.setValue("labourCessRRConstructionTotal", labourCessRRConstructionTotal);
        }
        if (field === "chargesForConstructioInSideMargineRRLandRate" || field === "area") {
            formMethods.setValue("chargesForConstructioInSideMargineRRLandTotal", chargesForConstructioInSideMargineRRLandTotal);
        }
        formMethods.setValue("totalAmount", totalAmount);
    };

    const onChangeParticular1Rate = (event: React.ChangeEvent<any>) => {
        const value = event.target.value.trim();
        // Remove non-numeric characters from the input value
        const numericValue = value.replace(/[^\d.]/g, "");
        formMethods.setValue("particular1Rate", numericValue);
        const particular1Total =
            Number(numericValue) * Number(formMethods?.getValues()?.area || 1);
        formMethods.setValue("particular1Total", particular1Total);
        const totalAmount =
            particular1Total +
            Number(formMethods?.getValues()?.particular2Total || 0) +
            Number(formMethods?.getValues()?.buildingDevelopmentRRLandTotal || 0) +
            Number(formMethods?.getValues()?.labourCessRRConstructionTotal || 0) +
            Number(formMethods?.getValues()?.chargesForConstructioInSideMargineRRLandTotal || 0) +
            Number(formMethods?.getValues()?.areaExcessOfBasicFSI || 0) +
            Number(formMethods?.getValues()?.rainWaterHarwesting || 0) +
            Number(formMethods?.getValues()?.scrutinyCharges || 0);
        formMethods.setValue("totalAmount", totalAmount);
    };

    const onChangeParticular1Total = (event: React.ChangeEvent<any>) => {
        const value = event.target.value.trim();
        // Remove non-numeric characters from the input value
        const numericValue = value.replace(/[^\d.]/g, "");
        formMethods.setValue("particular1Total", numericValue);
        const totalAmount =
            Number(numericValue) +
            Number(formMethods?.getValues()?.particular2Total || 0) +
            Number(formMethods?.getValues()?.buildingDevelopmentRRLandTotal || 0) +
            Number(formMethods?.getValues()?.labourCessRRConstructionTotal || 0) +
            Number(formMethods?.getValues()?.chargesForConstructioInSideMargineRRLandTotal || 0) +
            Number(formMethods?.getValues()?.areaExcessOfBasicFSI || 0) +
            Number(formMethods?.getValues()?.rainWaterHarwesting || 0) +
            Number(formMethods?.getValues()?.scrutinyCharges || 0);
        formMethods.setValue("totalAmount", totalAmount);
    };

    const onChangeParticular2Rate = (event: React.ChangeEvent<any>) => {
        const value = event.target.value.trim();
        // Remove non-numeric characters from the input value
        const numericValue = value.replace(/[^\d.]/g, "");
        formMethods.setValue("particular2Rate", numericValue);
        const particular2Total =
            Number(numericValue) * Number(formMethods?.getValues()?.area || 1);
        formMethods.setValue("particular2Total", particular2Total);
        const totalAmount =
            particular2Total +
            Number(formMethods?.getValues()?.particular1Total || 0) +
            Number(formMethods?.getValues()?.buildingDevelopmentRRLandTotal || 0) +
            Number(formMethods?.getValues()?.labourCessRRConstructionTotal || 0) +
            Number(formMethods?.getValues()?.chargesForConstructioInSideMargineRRLandTotal || 0) +
            Number(formMethods?.getValues()?.areaExcessOfBasicFSI || 0) +
            Number(formMethods?.getValues()?.rainWaterHarwesting || 0) +
            Number(formMethods?.getValues()?.scrutinyCharges || 0);
        formMethods.setValue("totalAmount", totalAmount);
    };

    const onChangeParticular2Total = (event: React.ChangeEvent<any>) => {
        const value = event.target.value.trim();
        // Remove non-numeric characters from the input value
        const numericValue = value.replace(/[^\d.]/g, "");
        formMethods.setValue("particular2Total", numericValue);
        const totalAmount =
            Number(numericValue) +
            Number(formMethods?.getValues()?.particular1Total || 0) +
            Number(formMethods?.getValues()?.buildingDevelopmentRRLandTotal || 0) +
            Number(formMethods?.getValues()?.labourCessRRConstructionTotal || 0) +
            Number(formMethods?.getValues()?.chargesForConstructioInSideMargineRRLandTotal || 0) +
            Number(formMethods?.getValues()?.areaExcessOfBasicFSI || 0) +
            Number(formMethods?.getValues()?.rainWaterHarwesting || 0) +
            Number(formMethods?.getValues()?.scrutinyCharges || 0);
        formMethods.setValue("totalAmount", totalAmount);
    };

    const onChangeScrutinyCharges = (event: React.ChangeEvent<any>) => {
        const value = event.target.value.trim();
        // Remove non-numeric characters from the input value
        const numericValue = value.replace(/[^\d.]/g, "");
        formMethods.setValue("scrutinyCharges", numericValue);
        const totalAmount =
            Number(numericValue) +
            Number(formMethods?.getValues()?.particular1Total || 0) +
            Number(formMethods?.getValues()?.particular2Total || 0) +
            Number(formMethods?.getValues()?.buildingDevelopmentRRLandTotal || 0) +
            Number(formMethods?.getValues()?.labourCessRRConstructionTotal || 0) +
            Number(formMethods?.getValues()?.chargesForConstructioInSideMargineRRLandTotal || 0) +
            Number(formMethods?.getValues()?.areaExcessOfBasicFSI || 0) +
            Number(formMethods?.getValues()?.rainWaterHarwesting || 0);
        formMethods.setValue("totalAmount", totalAmount);
    };

    const onChangeBuildingDevelopmentRRLandRate = (event: React.ChangeEvent<any>) => {
        const value = event.target.value.trim();
        // Remove non-numeric characters from the input value
        const numericValue = value.replace(/[^\d.]/g, "");
        formMethods.setValue("buildingDevelopmentRRLandRate", numericValue);
        const buildingDevelopmentRRLandTotal =
            Number(numericValue) * Number(formMethods?.getValues()?.area || 1);
        formMethods.setValue("buildingDevelopmentRRLandTotal", buildingDevelopmentRRLandTotal);
        const totalAmount =
            buildingDevelopmentRRLandTotal +
            Number(formMethods?.getValues()?.particular1Total || 0) +
            Number(formMethods?.getValues()?.particular2Total || 0) +
            Number(formMethods?.getValues()?.labourCessRRConstructionTotal || 0) +
            Number(formMethods?.getValues()?.chargesForConstructioInSideMargineRRLandTotal || 0) +
            Number(formMethods?.getValues()?.areaExcessOfBasicFSI || 0) +
            Number(formMethods?.getValues()?.rainWaterHarwesting || 0) +
            Number(formMethods?.getValues()?.scrutinyCharges || 0);
        formMethods.setValue("totalAmount", totalAmount);
    };

    const onChangeBuildingDevelopmentRRLandTotal = (event: React.ChangeEvent<any>) => {
        const value = event.target.value.trim();
        // Remove non-numeric characters from the input value
        const numericValue = value.replace(/[^\d.]/g, "");
        formMethods.setValue("buildingDevelopmentRRLandTotal", numericValue);
        const totalAmount =
            Number(numericValue) +
            Number(formMethods?.getValues()?.particular1Total || 0) +
            Number(formMethods?.getValues()?.particular2Total || 0) +
            Number(formMethods?.getValues()?.labourCessRRConstructionTotal || 0) +
            Number(formMethods?.getValues()?.chargesForConstructioInSideMargineRRLandTotal || 0) +
            Number(formMethods?.getValues()?.areaExcessOfBasicFSI || 0) +
            Number(formMethods?.getValues()?.rainWaterHarwesting || 0) +
            Number(formMethods?.getValues()?.scrutinyCharges || 0);
        formMethods.setValue("totalAmount", totalAmount);
    };

    const onChangeLabourCessRRConstructionRate = (event: React.ChangeEvent<any>) => {
        const value = event.target.value.trim();
        // Remove non-numeric characters from the input value
        const numericValue = value.replace(/[^\d.]/g, "");
        formMethods.setValue("labourCessRRConstructionRate", numericValue);
        const labourCessRRConstructionTotal =
            Number(numericValue) * Number(formMethods?.getValues()?.area || 1);
        formMethods.setValue("labourCessRRConstructionTotal", labourCessRRConstructionTotal);
        const totalAmount =
            labourCessRRConstructionTotal +
            Number(formMethods?.getValues()?.particular1Total || 0) +
            Number(formMethods?.getValues()?.particular2Total || 0) +
            Number(formMethods?.getValues()?.buildingDevelopmentRRLandTotal || 0) +
            Number(formMethods?.getValues()?.chargesForConstructioInSideMargineRRLandTotal || 0) +
            Number(formMethods?.getValues()?.areaExcessOfBasicFSI || 0) +
            Number(formMethods?.getValues()?.rainWaterHarwesting || 0) +
            Number(formMethods?.getValues()?.scrutinyCharges || 0);
        formMethods.setValue("totalAmount", totalAmount);
    };

    const onChangeLabourCessRRConstructionTotal = (event: React.ChangeEvent<any>) => {
        const value = event.target.value.trim();
        // Remove non-numeric characters from the input value
        const numericValue = value.replace(/[^\d.]/g, "");
        formMethods.setValue("labourCessRRConstructionTotal", numericValue);
        const totalAmount =
            Number(numericValue) +
            Number(formMethods?.getValues()?.particular1Total || 0) +
            Number(formMethods?.getValues()?.particular2Total || 0) +
            Number(formMethods?.getValues()?.buildingDevelopmentRRLandTotal || 0) +
            Number(formMethods?.getValues()?.chargesForConstructioInSideMargineRRLandTotal || 0) +
            Number(formMethods?.getValues()?.areaExcessOfBasicFSI || 0) +
            Number(formMethods?.getValues()?.rainWaterHarwesting || 0) +
            Number(formMethods?.getValues()?.scrutinyCharges || 0);
        formMethods.setValue("totalAmount", totalAmount);
    };

    const onChangeChargesForConstructioInSideMargineRRLandRate = (event: React.ChangeEvent<any>) => {
        const value = event.target.value.trim();
        // Remove non-numeric characters from the input value
        const numericValue = value.replace(/[^\d.]/g, "");
        formMethods.setValue("chargesForConstructioInSideMargineRRLandRate", numericValue);
        const chargesForConstructioInSideMargineRRLandTotal =
            Number(numericValue) * Number(formMethods?.getValues()?.area || 1);
        formMethods.setValue("chargesForConstructioInSideMargineRRLandTotal", chargesForConstructioInSideMargineRRLandTotal);
        const totalAmount =
            chargesForConstructioInSideMargineRRLandTotal +
            Number(formMethods?.getValues()?.particular1Total || 0) +
            Number(formMethods?.getValues()?.particular2Total || 0) +
            Number(formMethods?.getValues()?.buildingDevelopmentRRLandTotal || 0) +
            Number(formMethods?.getValues()?.labourCessRRConstructionTotal || 0) +
            Number(formMethods?.getValues()?.areaExcessOfBasicFSI || 0) +
            Number(formMethods?.getValues()?.rainWaterHarwesting || 0) +
            Number(formMethods?.getValues()?.scrutinyCharges || 0);
        formMethods.setValue("totalAmount", totalAmount);
    };

    const onChangeChargesForConstructioInSideMargineRRLandTotal = (event: React.ChangeEvent<any>) => {
        const value = event.target.value.trim();
        // Remove non-numeric characters from the input value
        const numericValue = value.replace(/[^\d.]/g, "");
        formMethods.setValue("buildingDevelopmentRRLandTotal", numericValue);
        const totalAmount =
            Number(numericValue) +
            Number(formMethods?.getValues()?.particular1Total || 0) +
            Number(formMethods?.getValues()?.particular2Total || 0) +
            Number(formMethods?.getValues()?.buildingDevelopmentRRLandTotal || 0) +
            Number(formMethods?.getValues()?.labourCessRRConstructionTotal || 0) +
            Number(formMethods?.getValues()?.areaExcessOfBasicFSI || 0) +
            Number(formMethods?.getValues()?.rainWaterHarwesting || 0) +
            Number(formMethods?.getValues()?.scrutinyCharges || 0);
        formMethods.setValue("totalAmount", totalAmount);
    };

    const onChangeAreaExcessOfBasicFSI = (event: React.ChangeEvent<any>) => {
        const value = event.target.value.trim();
        // Remove non-numeric characters from the input value
        const numericValue = value.replace(/[^\d.]/g, "");
        formMethods.setValue("areaExcessOfBasicFSI", numericValue);
        const totalAmount =
            Number(numericValue) +
            Number(formMethods?.getValues()?.particular1Total || 0) +
            Number(formMethods?.getValues()?.particular2Total || 0) +
            Number(formMethods?.getValues()?.buildingDevelopmentRRLandTotal || 0) +
            Number(formMethods?.getValues()?.labourCessRRConstructionTotal || 0) +
            Number(formMethods?.getValues()?.chargesForConstructioInSideMargineRRLandTotal || 0) +
            Number(formMethods?.getValues()?.rainWaterHarwesting || 0) +
            Number(formMethods?.getValues()?.securityDeposite || 0) +
            Number(formMethods?.getValues()?.scrutinyCharges || 0);
        formMethods.setValue("totalAmount", totalAmount);
    };

    const onChangeRainWaterHarwesting = (event: React.ChangeEvent<any>) => {
        const value = event.target.value.trim();
        // Remove non-numeric characters from the input value
        const numericValue = value.replace(/[^\d.]/g, "");
        formMethods.setValue("rainWaterHarwesting", numericValue);
        const totalAmount =
            Number(numericValue) +
            Number(formMethods?.getValues()?.particular1Total || 0) +
            Number(formMethods?.getValues()?.particular2Total || 0) +
            Number(formMethods?.getValues()?.buildingDevelopmentRRLandTotal || 0) +
            Number(formMethods?.getValues()?.labourCessRRConstructionTotal || 0) +
            Number(formMethods?.getValues()?.chargesForConstructioInSideMargineRRLandTotal || 0) +
            Number(formMethods?.getValues()?.areaExcessOfBasicFSI || 0) +
            Number(formMethods?.getValues()?.securityDeposite || 0) +
            Number(formMethods?.getValues()?.scrutinyCharges || 0);
        formMethods.setValue("totalAmount", totalAmount);
    };

    const onChangeSecurityDeposite = (event: React.ChangeEvent<any>) => {
        const value = event.target.value.trim();
        // Remove non-numeric characters from the input value
        const numericValue = value.replace(/[^\d.]/g, "");
        formMethods.setValue("securityDeposite", numericValue);
        const totalAmount =
            Number(numericValue) +
            Number(formMethods?.getValues()?.particular1Total || 0) +
            Number(formMethods?.getValues()?.particular2Total || 0) +
            Number(formMethods?.getValues()?.buildingDevelopmentRRLandTotal || 0) +
            Number(formMethods?.getValues()?.labourCessRRConstructionTotal || 0) +
            Number(formMethods?.getValues()?.chargesForConstructioInSideMargineRRLandTotal || 0) +
            Number(formMethods?.getValues()?.areaExcessOfBasicFSI || 0) +
            Number(formMethods?.getValues()?.rainWaterHarwesting || 0) +
            Number(formMethods?.getValues()?.scrutinyCharges || 0);
        formMethods.setValue("totalAmount", totalAmount);
    };

    const onChangeTotalAmount = (event: React.ChangeEvent<any>) => {
        const value = event.target.value.trim();
        // Remove non-numeric characters from the input value
        const numericValue = value.replace(/[^\d.]/g, "");
        formMethods.setValue("totalAmount", numericValue);
    };

    const [isEditableConditions, setIsEditableConditions] = useState(false);
    const toggleEditableConditions = () => {
        setIsEditableConditions((prev) => !prev);
    };

    return (
        <FormProvider {...formMethods}>
            <h4
                className={`text-center text-grey-600 font-semibold mb-8 text-2xl ${pdfToView ? "hidden" : ""
                    }`}
            >
                Demand Note - Building Plan 
            </h4>

            <form
                id="create-demand-note"
                className={`grid grid-cols-2 gap-4 ${pdfToView ? "hidden" : ""}`}
                noValidate
                onSubmit={formMethods.handleSubmit((data) =>
                    saveDataMutation.mutate(
                        {
                            applicationId: application.id,
                            reviewId,
                            data,
                            isThereAConst: isThereAConst ?? false,
                        },
                        {
                            onSuccess: (result) => {
                                if (Array.isArray(result?.data?.data)) {
                                    const blob = new Blob(
                                        [new Uint8Array(result.data.data).buffer],
                                        { type: "application/pdf" }
                                    );
                                    setPdfToView(URL.createObjectURL(blob));
                                }
                            },
                        }
                    )
                )}
            >
                <div className="col-span-2 md:col-span-1">
                    <Input
                        disabled
                        label="Applicant Name"
                        placeholder="Applicant Name"
                        {...formMethods.register("applicantName")}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        disabled
                        label="Layout Name"
                        placeholder="Layout Name"
                        {...formMethods.register("layoutName")}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        disabled
                        label="Mauza"
                        placeholder="Mauza"
                        {...formMethods.register("mauja")}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        disabled
                        label="Khasara No"
                        placeholder="Khasara No"
                        {...formMethods.register("khasaraNo")}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <div className="flex items-end justify-between gap-4">
                        <Input
                            disabled
                            type="number"
                            onWheel={(e) => {
                                e.currentTarget.blur();

                                e.stopPropagation();

                                setTimeout(() => {
                                    e.currentTarget.focus();
                                }, 0);
                            }}
                            containerClass="basis-3/5 md:basis-3/4"
                            label="Plot Area"
                            placeholder="Plot Area"
                            {...formMethods.register("area", { valueAsNumber: true })}
                        />
                        <Select
                            containerClass="basis-2/5 md:basis-1/4"
                            disabled
                            {...formMethods.register("unit_area")}
                        >
                            <option hidden></option>
                            <option value="sq. mt.">sq. mt.</option>
                            <option value="sq. ft.">sq. ft.</option>
                        </Select>
                    </div>
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        type="number"
                        onWheel={(e) => {
                            e.currentTarget.blur();

                            e.stopPropagation();

                            setTimeout(() => {
                                e.currentTarget.focus();
                            }, 0);
                        }}
                        disabled
                        label="Built-up Area [sq. mt.]"
                        placeholder="Built-up Area [sq. mt.]"
                        {...formMethods.register("constructionArea", {
                            valueAsNumber: true,
                        })}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        disabled
                        label="Plot No"
                        placeholder="Plot No"
                        {...formMethods.register("plotNo")}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        type="date"
                        label="Due Date"
                        placeholder="Due Date"
                        {...formMethods.register("dueDate", { valueAsDate: true })}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        required
                        type="number"
                        onWheel={(e) => {
                            e.currentTarget.blur();

                            e.stopPropagation();

                            setTimeout(() => {
                                e.currentTarget.focus();
                            }, 0);
                        }}
                        label="Rate for Regularization charges for residential plot"
                        placeholder="Rate for Regularization charges for residential plot"
                        error={!!formState.errors?.particular1Rate}
                        helperText={formState?.errors?.particular1Rate?.message?.toString()}
                        {...formMethods.register("particular1Rate", {
                            required: "* Required",
                            valueAsNumber: true,
                        })}
                        onChange={onChangeParticular1Rate}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <label className="flex items-center">
                        <span className="mr-2 p-1 bold">
                            <b>Regularization charges for residential plot Total</b>
                        </span>

                        <i
                            className={`fa ${isEditable.particular1Total ? "fa-lock" : "fa-pencil"
                                } cursor-pointer`}
                            onClick={() => toggleEditable("particular1Total")}
                        ></i>
                    </label>
                    <Input
                        required
                        type="number"
                        onWheel={(e) => {
                            e.currentTarget.blur();

                            e.stopPropagation();

                            setTimeout(() => {
                                e.currentTarget.focus();
                            }, 0);
                        }}
                        label=""
                        placeholder="Regularization charges for residential plot Total"
                        error={!!formState.errors?.particular1Total}
                        helperText={formState?.errors?.particular1Total?.message?.toString()}
                        {...formMethods.register("particular1Total", {
                            required: "* Required",
                            valueAsNumber: true,
                        })}
                        // onChange={(e) => onChangeHandler('particular1Total', e)}
                        onChange={onChangeParticular1Total}
                        disabled={!isEditable.particular1Total}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        required
                        type="number"
                        onWheel={(e) => {
                            e.currentTarget.blur();

                            e.stopPropagation();

                            setTimeout(() => {
                                e.currentTarget.focus();
                            }, 0);
                        }}
                        label="Rate for Development charges for residential plot"
                        placeholder="Rate for Development charges for residential plot"
                        error={!!formState.errors?.particular2Rate}
                        helperText={formState?.errors?.particular2Rate?.message?.toString()}
                        {...formMethods.register("particular2Rate", {
                            required: "* Required",
                            valueAsNumber: true,
                        })}
                        onChange={onChangeParticular2Rate}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <label className="flex items-center">
                        <span className="mr-2 p-1">
                            <b>Development charges for residential plot Total</b>{" "}
                        </span>
                        <i
                            className={`fa ${isEditable.particular2Total ? "fa-lock" : "fa-pencil"
                                } cursor-pointer`}
                            onClick={() => toggleEditable("particular2Total")}
                        ></i>
                    </label>

                    <Input
                        required
                        type="number"
                        onWheel={(e) => {
                            e.currentTarget.blur();
                            e.stopPropagation();
                            setTimeout(() => {
                                e.currentTarget.focus();
                            }, 0);
                        }}
                        label=""
                        placeholder="Development charges for residential plot Total"
                        error={!!formState.errors?.particular2Total}
                        helperText={formState?.errors?.particular2Total?.message?.toString()}
                        {...formMethods.register("particular2Total", {
                            required: "* Required",
                            valueAsNumber: true,
                        })}
                        // onChange={(e) => onChangeHandler('particular2Total', e)}
                        onChange={onChangeParticular2Total}
                        disabled={!isEditable.particular2Total}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        required
                        type="number"
                        onWheel={(e) => {
                            e.currentTarget.blur();

                            e.stopPropagation();

                            setTimeout(() => {
                                e.currentTarget.focus();
                            }, 0);
                        }}
                        label="Scrutiny Charges"
                        placeholder="Scrutiny Charges"
                        error={!!formState.errors?.scrutinyCharges}
                        helperText={formState?.errors?.scrutinyCharges?.message?.toString()}
                        {...formMethods.register("scrutinyCharges", {
                            required: "* Required",
                            valueAsNumber: true,
                        })}
                        onChange={onChangeScrutinyCharges}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        required
                        type="number"
                        label="Building Development RR Land Rate"
                        placeholder="Building Development RR Land Rate"
                        error={!!formState.errors?.buildingDevelopmentRRLandRate}
                        helperText={formState?.errors?.buildingDevelopmentRRLandRate?.message?.toString()}
                        {...formMethods.register("buildingDevelopmentRRLandRate", {
                            required: "* Required",
                            valueAsNumber: true,
                        })}
                        onChange={onChangeBuildingDevelopmentRRLandRate}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <label className="flex items-center">
                        <span className="mr-2 p-1 bold">
                            <b>Building Development RR Land Total</b>
                        </span>
                        <i
                            className={`fa ${isEditable.buildingDevelopmentRRLandTotal ? "fa-lock" : "fa-pencil"} cursor-pointer`}
                            onClick={() => toggleEditable("buildingDevelopmentRRLandTotal")}
                        ></i>
                    </label>
                    <Input
                        required
                        type="number"
                        placeholder="Building Development RR Land Total"
                        error={!!formState.errors?.buildingDevelopmentRRLandTotal}
                        helperText={formState?.errors?.buildingDevelopmentRRLandTotal?.message?.toString()}
                        {...formMethods.register("buildingDevelopmentRRLandTotal", {
                            required: "* Required",
                            valueAsNumber: true,
                        })}
                        onChange={(e) => onChangeHandler('buildingDevelopmentRRLandTotal', e)}
                        disabled={!isEditable.buildingDevelopmentRRLandTotal}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        required
                        type="number"
                        label="Labour cess R R Construction Rate"
                        placeholder="Labour cess R R Construction Rate"
                        error={!!formState.errors?.labourCessRRConstructionRate}
                        helperText={formState?.errors?.labourCessRRConstructionRate?.message?.toString()}
                        {...formMethods.register("labourCessRRConstructionRate", {
                            required: "* Required",
                            valueAsNumber: true,
                        })}
                        onChange={onChangeLabourCessRRConstructionRate}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <label className="flex items-center">
                        <span className="mr-2 p-1 bold">
                            <b>Labour cess R R Construction Total</b>
                        </span>
                        <i
                            className={`fa ${isEditable.labourCessRRConstructionTotal ? "fa-lock" : "fa-pencil"} cursor-pointer`}
                            onClick={() => toggleEditable("labourCessRRConstructionTotal")}
                        ></i>
                    </label>
                    <Input
                        required
                        type="number"
                        placeholder="Labour cess R R Construction Total"
                        error={!!formState.errors?.labourCessRRConstructionTotal}
                        helperText={formState?.errors?.labourCessRRConstructionTotal?.message?.toString()}
                        {...formMethods.register("labourCessRRConstructionTotal", {
                            required: "* Required",
                            valueAsNumber: true,
                        })}
                        onChange={(e) => onChangeHandler('labourCessRRConstructionTotal', e)}
                        disabled={!isEditable.labourCessRRConstructionTotal}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        required
                        type="number"
                        label="Charges For Construction In Side Margine R R Land Rate"
                        placeholder="Charges For Construction In Side Margine R R Land Rate"
                        error={!!formState.errors?.chargesForConstructioInSideMargineRRLandRate}
                        helperText={formState?.errors?.chargesForConstructioInSideMargineRRLandRate?.message?.toString()}
                        {...formMethods.register("chargesForConstructioInSideMargineRRLandRate", {
                            required: "* Required",
                            valueAsNumber: true,
                        })}
                        onChange={onChangeChargesForConstructioInSideMargineRRLandRate}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <label className="flex items-center">
                        <span className="mr-2 p-1 bold">
                            <b>Charges For Construction In Side Margine R R Land Total</b>
                        </span>
                        <i
                            className={`fa ${isEditable.chargesForConstructioInSideMargineRRLandTotal ? "fa-lock" : "fa-pencil"} cursor-pointer`}
                            onClick={() => toggleEditable("chargesForConstructioInSideMargineRRLandTotal")}
                        ></i>
                    </label>
                    <Input
                        required
                        type="number"
                        placeholder="Charges For Construction In Side Margine R R Land Total"
                        error={!!formState.errors?.chargesForConstructioInSideMargineRRLandTotal}
                        helperText={formState?.errors?.chargesForConstructioInSideMargineRRLandTotal?.message?.toString()}
                        {...formMethods.register("chargesForConstructioInSideMargineRRLandTotal", {
                            required: "* Required",
                            valueAsNumber: true,
                        })}
                        onChange={(e) => onChangeHandler('chargesForConstructioInSideMargineRRLandTotal', e)}
                        disabled={!isEditable.chargesForConstructioInSideMargineRRLandTotal}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        required
                        type="number"
                        onWheel={(e) => {
                            e.currentTarget.blur();

                            e.stopPropagation();

                            setTimeout(() => {
                                e.currentTarget.focus();
                            }, 0);
                        }}
                        label="Area Excess Of Basic FSI"
                        placeholder="Area Excess Of Basic FSI"
                        error={!!formState.errors?.areaExcessOfBasicFSI}
                        helperText={formState?.errors?.areaExcessOfBasicFSI?.message?.toString()}
                        {...formMethods.register("areaExcessOfBasicFSI", {
                            required: "* Required",
                            valueAsNumber: true,
                        })}
                        onChange={onChangeAreaExcessOfBasicFSI}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        required
                        type="number"
                        onWheel={(e) => {
                            e.currentTarget.blur();

                            e.stopPropagation();

                            setTimeout(() => {
                                e.currentTarget.focus();
                            }, 0);
                        }}
                        label="Rain Water Harwesting"
                        placeholder="Rain Water Harwesting"
                        error={!!formState.errors?.rainWaterHarwesting}
                        helperText={formState?.errors?.rainWaterHarwesting?.message?.toString()}
                        {...formMethods.register("rainWaterHarwesting", {
                            required: "* Required",
                            valueAsNumber: true,
                        })}
                        onChange={onChangeRainWaterHarwesting}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        required
                        type="number"
                        onWheel={(e) => {
                            e.currentTarget.blur();

                            e.stopPropagation();

                            setTimeout(() => {
                                e.currentTarget.focus();
                            }, 0);
                        }}
                        label="Security Deposite"
                        placeholder="Security Deposite"
                        error={!!formState.errors?.securityDeposite}
                        helperText={formState?.errors?.securityDeposite?.message?.toString()}
                        {...formMethods.register("securityDeposite", {
                            required: "* Required",
                            valueAsNumber: true,
                        })}
                        onChange={onChangeSecurityDeposite}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <label className="flex items-center">
                        <span className="mr-2 p-1">
                            <b> Total Demand Note Amount</b>{" "}
                        </span>
                        <i
                            className={`fa ${isEditable.totalAmount ? "fa-lock" : "fa-pencil"
                                } m-1 cursor-pointer`}
                            onClick={() => toggleEditable("totalAmount")}
                        ></i>
                    </label>
                    <Input
                        required
                        type="number"
                        onWheel={(e) => {
                            e.currentTarget.blur();

                            e.stopPropagation();

                            setTimeout(() => {
                                e.currentTarget.focus();
                            }, 0);
                        }}
                        label=""
                        placeholder="Total Demand Note Amount"
                        error={!!formState.errors?.totalAmount}
                        helperText={formState?.errors?.totalAmount?.message?.toString()}
                        {...formMethods.register("totalAmount", {
                            required: "* Required",
                            valueAsNumber: true,
                        })}
                        // onChange={(e) => onChangeHandler('totalAmount', e)}
                        onChange={onChangeTotalAmount}
                        disabled={!isEditable.totalAmount}
                    />
                </div>

                <div className="col-span-2 md:col-span-1">
                    <Input
                        disabled
                        label="Municipal Council Name"
                        placeholder="Municipal Council Name"
                        error={!!formState.errors?.municipality}
                        helperText={formState?.errors?.municipality?.message?.toString()}
                        {...formMethods.register("municipality", {
                            required: "* Required",
                        })}
                    />
                </div>

                <div className="col-span-2 md:col-span-1">
                    <Input
                        disabled
                        label="Office Address: Tahasil"
                        placeholder="Office Address: Tahasil"
                        error={!!formState.errors?.tehsil}
                        helperText={formState?.errors?.tehsil?.message?.toString()}
                        {...formMethods.register("tehsil", { required: "* Required" })}
                    />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Input
                        disabled
                        label="Office Address: District"
                        placeholder="Office Address: District"
                        error={!!formState.errors?.district}
                        helperText={formState?.errors?.district?.message?.toString()}
                        {...formMethods.register("district", { required: "* Required" })}
                    />
                </div>
                <div className="col-span-2">
                    <label className="flex items-center">
                        <span className="text-base font-bold mb-2">
                            Conditions For Demand Note
                        </span>
                        <i
                            className={`fa ${isEditableConditions ? "fa-lock" : "fa-pencil"
                                } cursor-pointer ml-2`}
                            onClick={toggleEditableConditions}
                        ></i>
                    </label>
                    <textarea
                        placeholder="Conditions for Demand Note"
                        {...formMethods.register("conditions")}
                        rows={5}
                        className={`h-auto w-100 ${isEditableConditions ? "bg-white" : "bg-gray-200"
                            }`}
                        disabled={!isEditableConditions}
                    />
                </div>
                <div className="col-span-2 flex justify-center space-x-4 items-center mt-4">
                    <button
                        type="button"
                        onClick={onBack}
                        disabled={saveDataMutation.isPending || forwardMutation.isPending}
                        className="bg-black px-4 md:px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
                    >
                        Back
                    </button>
                    <button
                        form="create-demand-note"
                        type="submit"
                        disabled={saveDataMutation.isPending || forwardMutation.isPending}
                        className="bg-primary px-4 md:px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
                    >
                        {"Save And Preview"}
                    </button>
                </div>
            </form>
            {!!pdfToView && (
                <>
                    <iframe
                        title="PDF Viewer"
                        // className="w-full h-full min-h-screen hidden sm:block"
                        className={style.IFrameCss}
                        src={pdfToView + "#toolbar=0"}
                    />
                    <div className="flex items-center justify-center sm:hidden py-36 bg-gray-200 flex-col gap-4">
                        <p>Demand Note</p>
                        <a
                            className="inline-block text-white py-2 px-4 rounded-full bg-blue-500"
                            download={`${application.applicationId}-Demand-Note.pdf`}
                            href={pdfToView}
                        >
                            Download
                        </a>
                    </div>
                    <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-8">
                        <button
                            type="button"
                            onClick={() => setPdfToView(null)}
                            disabled={saveDataMutation.isPending || forwardMutation.isPending}
                            className="bg-primary px-6 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500 w-full sm:w-auto"
                        >
                            Back
                        </button>
                        <button
                            type="button"
                            className="bg-primary px-6 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500 w-full sm:w-auto"
                        >
                            <a href={pdfToView} download className="hover:text-white">Download</a>
                        </button>
                        <button
                            form="create-demand-note"
                            type="submit"
                            onClick={(data) => {
                                onDownload()
                            }}
                            disabled={saveDataMutation.isPending || forwardMutation.isPending}
                            className="bg-primary px-6 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500 w-full sm:w-auto"
                        >
                            {forwardMutation.isPending ? "Next" : "Next"}
                        </button>
                    </div>
                </>
            )}
        </FormProvider>
    );
}

function UploadDemandNoteWrapper({
    application,
    onBack,
}: GenerateDemandNoteProps) {
    const isThereAConst = application.is_there_a_construction;
    console.log("yes/no", isThereAConst)
    
    const fileTypes = ["JPG", "PNG", "GIF", "PDF"];
    const navigate = useNavigate();
    const [file, setFile] = useState<any>()
    const [isModal, setIsModal] = useState(false)
    const lastReviewQuery = useLevel3LastReview({ id: application.id });
    const reviewId = lastReviewQuery.data?.review?.id;
    const forwardMutation = useForwardApplicationForLevel3();
    const saveDataMutation = useSaveDemandNoteDataForLevel3();
    const savedDemandNoteDataQuery = useSavedDemandNoteDataForLevel3({
        applicationId: application.id,
        reviewId: reviewId,
    });

    const handleFile = (e: any) => {
        setFile(e)
    }
    const isFetching =
        lastReviewQuery.isFetching || savedDemandNoteDataQuery.isFetching;
    const isError =
        lastReviewQuery.isError || savedDemandNoteDataQuery.isError || !reviewId;

    if (isFetching || isError) {
        return (
            <div className="flex justify-center items-center p-8">
                {isError ? (
                    <p>Something went wrong while fetching info!</p>
                ) : (
                    <SpinnerCircular />
                )}
            </div>
        );
    }

    const onUploadAndForward = async () => {

        const demandNoteURL = await uploadFileToBlob(file, 'demand_note', 'application_id', application.applicationId);

        _axios.post(`${BASE_URL}/api/digital-signature-docs/save`,
            {
                submitted_by: 'Level 3',
                file_url: demandNoteURL,
                application_id: application.applicationId,
                doc_type: 'demand note'
            })
            .then(function (details) {
                forwardMutation.mutate(
                    { applicationId: application.id, reviewId, isThereAConst: isThereAConst ?? false, },
                    {
                        onSuccess: (result) => navigate("/level-three-applications"),
                    }
                )

            }).catch(function (error) {
                // handle error
                console.log(error);
            })


    }

    return (
        <div className="w-full">
            <h2 className="text-2xl mb-10">Upload Demand Note After Digital Signature</h2>
            <div className='flex flex-col md:flex-row' style={{ paddingBottom: '20px' }}>
                <FileUploader handleChange={handleFile} name="file" types={fileTypes} children={
                    <div className={style.singleUpload}>
                        <div className='flex rounded-md border-inherit border p-2'>
                            <button className='px-7 py-2 text-white bg-yellow-500 rounded-lg'>Browse</button>
                            <small className='pl-4 self-center'>Drag and Drop file here</small>
                        </div>
                    </div>
                } />
                {file && (
                    <div className='bg-slate-100 rounded-lg py-3 px-3 mx-0 md:mx-4 mt-4 md:mt-0 self-center h-65 md:h-65'
                        style={{ height: '400px', overflow: 'hidden' }}
                        onClick={() => setIsModal(true)}>
                        {file?.type !== 'application/pdf' ?
                            <img src={URL.createObjectURL(file)} alt="Logo" className={`${style.cloudUpload} w-full`} />
                            :
                            <div style={{ height: '370px', width: '280px' }}>
                                <Worker workerUrl={`https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js`}>
                                    <Viewer fileUrl={URL.createObjectURL(file)} />
                                </Worker>
                            </div>
                        }
                    </div>
                )}
                {file &&
                    <div className="inline-block"><DeleteButton onClick={() => setFile(null)} /></div>
                    // <i
                    //     onClick={() => setFile(null)}
                    //     className="fa fa fa-trash self-start mt-2 md:mt-0 md:self-center"
                    //     style={{ color: "red" }}
                    // ></i>
                }
            </div>

            <div className="col-span-2 flex justify-center space-x-4 items-center mt-8">
                <button
                    type="button"
                    onClick={onBack}
                    disabled={saveDataMutation.isPending || forwardMutation.isPending}
                    className="bg-black px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
                >
                    Back
                </button>
                <button
                    form="create-demand-note"
                    type="button"
                    onClick={onUploadAndForward}
                    disabled={!file}
                    className="bg-primary px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
                >
                    {forwardMutation.isPending ? "Forwarding" : "Upload and Forward"}

                </button>
            </div>
            <DocumentPreviewDialog onClose={() => setIsModal(false)} open={isModal}>
                {file && (
                    <Worker workerUrl={`https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js`}>
                        <Viewer fileUrl={URL.createObjectURL(file)} />
                    </Worker>
                )}
            </DocumentPreviewDialog>

        </div>
    );
}

type ReviewProps = {
    steps?: TPopulatedForm[];
    onBack: () => unknown;
    onNext: () => unknown;
    application: TApplication & Record<string, unknown>;
    allDocsApproved?: boolean;
};

function Review({
    allDocsApproved,
    steps,
    onBack,
    onNext,
    application,
}: ReviewProps) {
    const qc = useQueryClient()
    const navigate = useNavigate()
    const [reviewsDialog, toggleReviewsDialog] = useToggle();
    const allReviews = useLevel3AllReviews({ id: application.id });
    const saveReviewMutation = useSaveLevel3Review();
    const lastReviewQuery = useLevel3LastReview({ id: application.id });
    const [fieldsToChange, setFieldsToChange] = useState<number[]>([]);
    const [comment, setComment] = useState("");
    const rejectMutation = useLevel3RejectApplication();
    const returnMutation = useLevel3ReturnApplication();
    const {
        data: overrideData
    } = useApplicationOverride(application?.applicationId);
    const disabled = false
    // lastReviewQuery.isFetching ||
    // saveReviewMutation.isPending ||
    // rejectMutation.isPending ||
    // returnMutation.isPending ||
    // application.status !== "Pending Level 3 Review";

    const lastReviewData = lastReviewQuery.data;

    const allFields = useMemo(
        () => (steps || []).flatMap((e) => e.fields || []).filter((e) => e?.active),
        [steps]
    );

    const defaultSelected = useMemo(() => {
        return allFields.filter((e) =>
            lastReviewData?.fieldsToChange?.includes(e.id)
        );
    }, [allFields, lastReviewData]);

    useEffect(() => {
        setComment(lastReviewData?.review?.comment_text || "");
        setFieldsToChange(lastReviewData?.fieldsToChange || []);
    }, [lastReviewData]);

    if (lastReviewQuery.isLoading || lastReviewQuery.isError) {
        return (
            <div className="flex justify-center items-center p-8">
                {lastReviewQuery.isError ? (
                    <p>Something went wrong while fetching info!</p>
                ) : (
                    <SpinnerCircular />
                )}
            </div>
        );
    }

    let level1ReviewInfoToRender = null;

    if (allReviews.isLoading || allReviews.isError) {
        level1ReviewInfoToRender = (
            <div className="flex justify-center items-center p-8">
                {allReviews.isError ? (
                    <p>Something went wrong while fetching info!</p>
                ) : (
                    <SpinnerCircular />
                )}
            </div>
        );
    } else {
        const level1Review = (
            allReviews.data || []
        ).reduce<TReviewWithReviewer | null>(
            (acc, e) => (e?.level === "Level 1" || e?.level === "Level 2" || e?.level === "Level 3" ? e : acc),
            null
        );
        if (level1Review) {
            level1ReviewInfoToRender = (
                <div className="mb-8">
                    {overrideData?.data ?
                        <div>
                            <div className="flex justify-between">
                                <h4 className="text-center text-grey-600 font-semibold mb-8 text-xl sm:text-2xl lg:text-2xl mr-2">
                                    Application Details
                                </h4>
                                <div className=" bg-blue-500 rounded-lg p-2">
                                    <p className="text-white">Overridden by: {overrideData?.data?.override_by}</p>
                                    <p className="text-white">Date: {formatDate(new Date(overrideData?.data?.updated_date))}</p>
                                </div>
                            </div>
                            {/* <h5 className="py-5 font-semibold">Override reason</h5>
                <p className="pb-7">{overrideData?.data?.reason}</p> */}
                        </div>
                        : <div className="flex mb-4">
                            <h4 className="text-xl font-bold mb-4">Previous Reviewer Remark</h4>
                            {/* <img src={userImg} height={36} width={36} alt="profile-pic" />
                        <div>
                            <p className="text-base font-semibold">
                                {level1Review.reviewer?.name || "--"}
                            </p>
                            <p className="text-xs">{formatDate(level1Review.date || "")}</p>
                        </div> */}
                        </div>}
                    <div className="flex justify-between  items-center space-x-4">
                        <div className="grow">
                            <h6 className="mb-2 text-lg font-bold">
                                {`${level1Review?.level} Reviewer Remark`}
                            </h6>
                            <p className="text-base">{level1Review.comment_text}</p>
                        </div>
                        <img src={userImg} height={36} width={36} alt="profile-pic" />
                        <div>
                            <p className="text-base font-semibold">
                                {level1Review.reviewer?.name || "--"}
                            </p>
                            <p className="text-xs">{formatDate(level1Review.date || "")}</p>
                        </div>
                    </div>
                </div>
            );
        }
    }
    console.log('lastReviewQuery', lastReviewQuery)

    const saveReview = (cb?: () => unknown) => {
        saveReviewMutation.mutate({
            applicationId: application.id,
            comment,
            fieldsToChange,
        }, { onSuccess: cb });
    }

    return (
        <>
            {level1ReviewInfoToRender}
            <div className="grid grid-cols-5 gap-4 md:gap-8 items-center">
                <div className="col-span-5 md:col-span-4">
                    <div className="flex justify-between items-center mb-2">
                        <label className="text-sm md:text-base font-bold">
                            Level 3 Remark <span className="text-red-500">*</span>
                        </label>
                        <button
                            className="text-sm md:text-base font-bold disabled:text-gray-500"
                            disabled={!allReviews.data?.length}
                            onClick={toggleReviewsDialog}
                        >
                            <i className="fa fa-history"></i>
                            Previous Reviews
                        </button>
                    </div>
                    <textarea
                        placeholder="Add Comment"
                        value={comment}
                        disabled={disabled}
                        rows={5}
                        className="h-auto w-100"
                        onChange={(e) => setComment(e.target.value)}
                    />
                    <Multiselect
                        disable={disabled}
                        key={`multiselect-${lastReviewQuery.isLoading}`}
                        style={{
                            multiselectContainer: {
                                border: "1px solid #dee2e6",
                                "border-radius": "8px",
                            },
                        }}
                        selectedValues={defaultSelected}
                        placeholder="Select Change Fields"
                        options={allFields}
                        onSelect={(list: TApplicationField[]) =>
                            setFieldsToChange(list.map((e) => e.id))
                        }
                        onRemove={(list: TApplicationField[]) =>
                            setFieldsToChange(list.map((e) => e.id))
                        }
                        displayValue="label"
                    />

                    <div className="flex-wrap md:flex-nowrap flex justify-center gap-4 items-center mt-8">
                        <button
                            type="button"
                            onClick={onBack}
                            disabled={saveReviewMutation.isPending}
                            className="bg-black px-10 py-2 rounded-lg md:rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500 basis-full md:basis-auto"
                        >
                            Back
                        </button>
                        <button
                            type="button"
                            disabled={disabled || !comment?.trim()}
                            onClick={() => saveReview()}
                            className="bg-primary px-10 py-2 rounded-lg md:rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500 basis-full md:basis-auto"
                        >
                            {saveReviewMutation.isPending ? "Saving" : "Save"}
                        </button>

                    </div>
                </div>
                <div className="col-span-5 md:col-span-1 space-y-4 mb-10">
                    <button
                        type="button"
                        disabled={
                            disabled ||
                            !lastReviewData?.review?.id ||
                            !allDocsApproved ||
                            !!fieldsToChange?.length
                        }
                        onClick={onNext}
                        className="bg-primary w-full py-2 rounded-lg font-bold text-white block disabled:bg-gray-300 disabled:text-gray-500"
                    >
                        Approve & Next
                    </button>
                    <button
                        type="button"
                        disabled={disabled || !lastReviewQuery.data?.review?.id || (allDocsApproved && !fieldsToChange.length)}
                        className="bg-red-400 w-full py-2 rounded-lg font-bold text-white block disabled:bg-gray-300 disabled:text-gray-500"
                        onClick={() =>
                            saveReview(() => returnMutation.mutate({ applicationId: application.id, application_id: application.applicationId, user_id: application.userId }, {
                                onSuccess: () => {
                                    navigate('/level-three-applications')
                                    qc.invalidateQueries({ queryKey: level3ApplicationsKeyStore.id(application.id) })
                                }
                            }))
                        }
                    >
                        {returnMutation.isPending ? "Returning" : "Return"}
                    </button>
                    <button
                        type="button"
                        disabled={disabled || !lastReviewQuery.data?.review?.id}
                        className="bg-red-600 w-full py-2 rounded-lg font-bold text-white block disabled:bg-gray-300 disabled:text-gray-500"
                        onClick={() =>
                            saveReview(() => rejectMutation.mutate({ applicationId: application.id, application_id: application.applicationId, user_id: application.userId }, {
                                onSuccess: () => {
                                    navigate('/level-three-applications')
                                    qc.invalidateQueries({ queryKey: level3ApplicationsKeyStore.id(application.id) })
                                }
                            }))
                        }
                    >
                        {rejectMutation.isPending ? "Rejecting" : "Reject"}
                    </button>
                </div>
            </div>
            {reviewsDialog && (
                <RenderAllReviews
                    onClose={toggleReviewsDialog}
                    reviews={allReviews.data || []}
                />
            )}
        </>
    );
}

type ViewAndValidateDocumentsAndNOCsProps = {
    nocDecisions?: Record<string, boolean>;
    documentDecisions?: Record<string, boolean>;
    steps?: TPopulatedForm[];
    application: TApplication & Record<string, unknown>;
    onNext?: () => unknown;
};

function ViewAndValidateDocumentsAndNOCs({
    steps,
    application,

    nocDecisions,
    documentDecisions,
    onNext,
}: ViewAndValidateDocumentsAndNOCsProps) {
    const status = application?.status;
    const id = application?.id;
    const [document, setDocument] = useState<{
        name: string;
        url: string;
    } | null>(null);
    const navigate = useNavigate();
    const docDecisionMutation = useLevel3UpdateDocumentDecision();
    const nocDecisionMutation = useLevel3UpdateNocDecision();
    const allReviews = useLevel3AllReviews({ id: id });
    const {
        data: overrideData
    } = useApplicationOverride(application?.applicationId);
    const { isPending: updatingNocDecision, mutate: updateNocDecision } =
        nocDecisionMutation;

    const nocColumns = useMemo((): TableColumn<Record<string, unknown>>[] => {
        const noc = steps?.find((e) => e.type === "NOC");
        if (!noc) return [];
        return [
            { name: "Sr. No.", selector: (_, i) => (i || 0) + 1, center: true, grow: 0.5 },
            {
                name: "NOC Type",
                selector: (d) =>
                    String(
                        (noc.fields?.find((e) => e.uid === "noc_type")?.options || []).find(
                            (e) => e.id === d.noc_type
                        )?.name || "-"
                    ),
                center: true,
                grow: 2,
                width: "190px",
            },
            {
                name: "NOC No.",
                selector: (d) => String(d?.noc_number || "-"),
                center: true,
                grow: 2,
                width: "180px",
            },
            {
                name: "NOC Date",
                selector: (d) => formatDate(String(d?.noc_date || "x")),
                center: true,
                grow: 2,
                width: "180px",
            },
            {
                name: "Level 1",
                center: true,
                cell: () => (<>
                    {overrideData?.data && ((allReviews.data?.length ?? 0) < 1 || !(allReviews as any)?.data?.some((review: any) => (review?.status === "Approved" && review?.level === "Level 1"))) ? <p> - </p> :
                        <i className="m-0 fa fa text-lg fa-check-square-o py-2 px-3 rounded-md bg-gray-400 text-gray-600"></i>
                    }
                </>
                ),
            },
            {
                name: "Level 2",
                center: true,
                cell: () => (<>
                    {overrideData?.data ? <p> - </p> :
                        <i className="m-0 fa fa text-lg fa-check-square-o py-2 px-3 rounded-md bg-gray-400 text-gray-600"></i>
                    }
                </>
                ),
            },
            {
                name: "View",
                width: "180px",
                grow: 2,
                center: true,
                cell: (d, i) => (
                    <div className="flex space-x-4">
                        {/* <button
                            className="text-lg"
                            disabled={
                                updatingNocDecision || status !== "Pending Level 3 Review"
                            }
                            onClick={() => {
                                updateNocDecision({
                                    applicationId: id,
                                    decision: nocDecisions?.[String(d?.id)] === false ? 1 : 0,
                                    nocId: Number(d.id),
                                });
                            }}
                        >
                            {nocDecisions?.[String(d?.id)] === false ? (
                                <i className="m-0 fa fa-window-close-o text-red-500"></i>
                            ) : (
                                <i className="m-0 fa fa-check-square-o text-green-400"></i>
                            )}
                        </button> */}

                        {/* <button
                            className="text-lg"
                            onClick={() =>
                                setDocument({
                                    url: String(d?.noc_doc),
                                    name: String(
                                        noc.fields
                                            ?.find((e) => e.uid === "noc_type")
                                            ?.options?.find((e) => e.id === d.noc_type)?.name || ""
                                    ),
                                })
                            }
                        >
                            <i className="m-0 fa fa-eye"></i>
                        </button> */}
                        {/* <button
                            onClick={() => downloadResource(String(d?.noc_doc))}
                            className="text-lg"
                        >
                            <i className="m-0 fa fa-download"></i>
                        </button> */}

                        <NocButton
                            isChecked={nocDecisions?.[String(d?.id)] !== false} // Change this line
                            isUpdating={updatingNocDecision}
                            statusCheck={status}
                            onClick={() => {
                                updateNocDecision({
                                    applicationId: id,
                                    decision: nocDecisions?.[String(d?.id)] === false ? 1 : 0,
                                    nocId: Number(d.id),
                                });
                            }}
                            tooltipText="Toggle NOC Decision"
                        />
                        <ViewButton
                            onClick={() =>
                                setDocument({
                                    url: String(d?.noc_doc),
                                    name: String(
                                        noc.fields
                                            ?.find((e) => e.uid === "noc_type")
                                            ?.options?.find((e) => e.id === d.noc_type)?.name || ""
                                    ),
                                })
                            }
                        />
                        <DownloadButton
                            onClick={() => downloadResource(String(d?.noc_doc))}
                        />
                    </div>
                ),
            },
        ];
    }, [status, steps, allReviews, updatingNocDecision, nocDecisions, updateNocDecision]);

    const { isPending: updatingDocDecision, mutate: updateDocDecision } =
        docDecisionMutation;

    const documentTable = useMemo((): {
        docs: { id: number; name: string; url: string }[];
        documentColumns: TableColumn<{
            id: number;
            name: string;
            url: string;
        }>[];
    } => {
        const docForm = steps?.find((e) => e.type === "Documents");

        if (!docForm) return { docs: [], documentColumns: [] };
        const isOverridden = Boolean(overrideData?.data);
        return {
            docs: Object.entries(application?.documents || {})
                .map(([k, { document: url, id: documentId }]) => {
                    const hit = docForm?.documentConfigs?.find(
                        (e) => e.document_config_id === +k
                    )?.document_label;

                    if (!hit) return null;
                    return { name: hit, url, id: documentId };
                })
                .filter((x): x is NonNullable<typeof x> => !!x),
            documentColumns: [
                { name: "Sr. No.", selector: (_, i) => (i || 0) + 1, center: true, grow: 0.5 },
                {
                    name: "Document Name",
                    // selector: (d) => d.name, 
                    cell: (d) => <span className="text-center">{d.name}</span>,
                    center: true,
                    grow: 3,
                    width: "380px"
                },
                {
                    name: "Level 1",
                    center: true,
                    cell: () =>
                        isOverridden && ((allReviews.data?.length ?? 0) < 1 || !(allReviews as any)?.data?.some((review: any) => (review?.status === "Approved" && review?.level === "Level 1"))) ? (
                            <span>-</span>
                        ) : (
                            <i className="m-0 fa fa text-lg fa-check-square-o py-2 px-3 rounded-md bg-gray-400 text-gray-600"></i>
                        ),
                },
                {
                    name: "Level 2",
                    center: true,
                    cell: () =>
                        isOverridden ? (
                            <span>-</span>
                        ) : (
                            <i className="m-0 fa fa text-lg fa-check-square-o py-2 px-3 rounded-md bg-gray-400 text-gray-600"></i>
                        ),
                },
                {
                    name: "View",
                    center: true,
                    width: "180px",
                    cell: (d, i) => (
                        <div className="flex space-x-4">
                            {/* <button
                                className="text-lg"
                                disabled={
                                    updatingDocDecision || status !== "Pending Level 3 Review"
                                }
                                onClick={() => {
                                    updateDocDecision({
                                        applicationId: id,
                                        decision:
                                            documentDecisions?.[String(d?.id)] === false ? 1 : 0,
                                        docId: Number(d.id),
                                    });
                                }}
                            >
                                {documentDecisions?.[String(d?.id)] === false ? (
                                    <i className="m-0 fa fa-window-close-o text-red-500"></i>
                                ) : (
                                    <i className="m-0 fa fa-check-square-o text-green-400"></i>
                                )}
                            </button>
                            <button
                                className="text-lg"
                                onClick={() =>
                                    setDocument({
                                        url: String(d?.url),
                                        name: String(d?.name || ""),
                                    })
                                }
                            >
                                <i className="m-0 fa fa-eye"></i>
                            </button>
                            <button
                                onClick={() => downloadResource(String(d?.url))}
                                className="text-lg"
                            >
                                <i className="m-0 fa fa-download"></i>
                            </button> */}
                            <DocDecisionL3Button
                                isChecked={documentDecisions?.[String(d?.id)] !== false}
                                isUpdating={updatingDocDecision}
                                statusCheck={status}
                                onClick={() => {
                                    updateDocDecision({
                                        applicationId: id,
                                        decision: documentDecisions?.[String(d?.id)] === false ? 1 : 0,
                                        docId: Number(d.id),
                                    });
                                }}
                                tooltipText="Toggle Document Decision"
                            />
                            <ViewButton
                                onClick={() =>
                                    setDocument({
                                        url: String(d?.url),
                                        name: String(d?.name || ""),
                                    })
                                }
                            />
                            <DownloadButton
                                onClick={() => downloadResource(String(d?.url))}
                            />
                        </div>
                    ),
                },
            ],
        };
    }, [
        steps,
        status,
        allReviews,
        application,
        updateDocDecision,
        updatingDocDecision,
        documentDecisions,
        overrideData?.data
    ]);

    const nothingToShow = !!(
        !(application?.nocs as Array<Record<string, unknown>>)?.length &&
        !(documentTable?.docs as Array<Record<string, unknown>>)?.length
    );


    return (
        <>
            {overrideData?.data ?
                <div>
                    <div className="flex justify-between">
                        <h4 className="text-center text-grey-600 font-semibold mb-8 text-xl sm:text-2xl lg:text-2xl mr-2">
                            Documents
                        </h4>
                        <div className=" bg-blue-500 rounded-lg p-2">
                            <p className="text-white">Overridden by: {overrideData?.data?.override_by}</p>
                            <p className="text-white">Date: {formatDate(new Date(overrideData?.data?.updated_date))}</p>
                        </div>
                    </div>
                    {/* <h5 className="py-5 font-semibold">Override reason</h5>
                <p className="pb-7">{overrideData?.data?.reason}</p> */}
                </div>
                : <h4 className="text-center text-grey-600 font-semibold mb-8 text-2xl">
                    Documents
                </h4>}
            {!!nocColumns?.length &&
                !!(application?.nocs as Record<string, unknown>[])?.length && (
                    <div>
                        <p className=" text-grey-600 text-base font-semibold">
                            Noc Table:
                        </p>
                        <DataTable
                            keyField="url"
                            className="mt-4 border border-none"
                            columns={nocColumns}
                            customStyles={tableCustomStyles}
                            data={(application?.nocs as Record<string, unknown>[]) || []}
                        />
                    </div>
                )}
            {!!documentTable.docs?.length && (
                <div>  <p className="text-grey-600 text-base mt-4 font-semibold">
                    Document Table:
                </p>
                    <DataTable
                        keyField="url"
                        className="mt-4 border border-none"
                        data={documentTable.docs}
                        customStyles={tableCustomStyles}
                        columns={documentTable.documentColumns}
                    />
                </div>
            )}
            {nothingToShow && (
                <div className="flex justify-center items-center py-8">
                    <h6 className="text-xl font-semibold">No Documents</h6>
                </div>
            )}
            {!!onNext && (
                <div className="flex justify-center space-x-4 items-center pt-8">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        disabled={
                            nocDecisionMutation.isPending || docDecisionMutation.isPending
                        }
                        className="bg-black ml-4 px-6 py-2 font-bold text-white rounded-full disabled:bg-gray-300"
                    >
                        Back
                    </button>
                    <button
                        type="button"
                        onClick={onNext}
                        disabled={
                            nocDecisionMutation.isPending || docDecisionMutation.isPending
                        }
                        className="bg-primary px-6 py-2 rounded-full font-bold text-white"
                    >
                        Next
                    </button>
                </div>
            )}
            {!!document && (
                <DocumentPreviewDialog
                    onClose={() => setDocument(null)}
                    open={!!document}
                >

                    {document?.url?.endsWith("pdf") ? (
                        <Worker workerUrl={`https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js`}>
                            <Viewer fileUrl={document.url} />
                        </Worker>
                    ) : (
                        <img
                            src={String(document?.url)}
                            className="object-contain w-full h-full"
                            alt="Document"
                        />
                    )}

                </DocumentPreviewDialog>
            )}

        </>
    );
}

export default LevelThreeApplicationDoc;
